import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { devError } from '../lib/logger';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Package, BookOpen, Layers, TrendingUp, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#C5A059', '#2D6A4F', '#5C5C5C', '#9E2A2B', '#A88332', '#E5E0D8', '#B38F48'];

function StatCard({ icon: Icon, label, value, sub, onClick }) {
  return (
    <div onClick={onClick} className={`bg-white border border-[#E5E0D8] rounded-sm p-6 ${onClick ? 'cursor-pointer hover:shadow-md' : ''} transition-shadow`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 rounded-sm bg-[#F2E8D5] flex items-center justify-center">
          <Icon size={20} className="text-[#C5A059]" />
        </div>
        {sub && <span className="text-xs text-[#5C5C5C]">{sub}</span>}
      </div>
      <p className="text-3xl font-semibold text-[#1A1A1A] mb-1">{value}</p>
      <p className="text-xs tracking-[0.15em] uppercase text-[#5C5C5C] font-medium">{label}</p>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/dashboard/stats');
      setStats(data);
    } catch (err) { devError('Dashboard load failed', err); toast.error('Failed to load dashboard'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return <div className="flex justify-center py-20"><div className="spinner-gold" /></div>;
  if (!stats) return null;

  return (
    <div data-testid="dashboard-page" className="animate-fade-in">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-[#1A1A1A] tracking-tight">Dashboard</h1>
        <p className="text-sm text-[#5C5C5C] mt-1">Overview of your product catalog system</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <StatCard icon={Package} label="Total Products" value={stats.total_products} sub={`${stats.active_products} active`} onClick={() => navigate('/products')} />
        <StatCard icon={BookOpen} label="Catalogs" value={stats.total_catalogs} onClick={() => navigate('/catalogs')} />
        <StatCard icon={Layers} label="Templates" value={stats.total_templates} />
        <StatCard icon={TrendingUp} label="Brands" value={stats.brand_distribution.length} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-6" data-testid="brand-chart">
          <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">Products by Brand</h3>
          {stats.brand_distribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.brand_distribution} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" tick={{ fontSize: 11, fill: '#5C5C5C' }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#5C5C5C' }} width={120} />
                <Tooltip contentStyle={{ border: '1px solid #E5E0D8', borderRadius: 2, fontSize: 12 }} />
                <Bar dataKey="count" fill="#C5A059" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-[#5C5C5C] text-center py-8">No product data yet</p>
          )}
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-sm p-6" data-testid="category-chart">
          <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">Products by Category</h3>
          {stats.category_distribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={stats.category_distribution} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, count }) => `${name} (${count})`} labelLine={false} style={{ fontSize: 10 }}>
                  {stats.category_distribution.map((entry, i) => <Cell key={`cat-cell-${entry.name}`} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ border: '1px solid #E5E0D8', borderRadius: 2, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-[#5C5C5C] text-center py-8">No product data yet</p>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-6" data-testid="recent-products">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg text-[#1A1A1A]">Recent Products</h3>
            <button onClick={() => navigate('/products')} className="text-xs text-[#C5A059] hover:underline flex items-center gap-1">View all <ArrowRight size={12} /></button>
          </div>
          {stats.recent_products.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_products.map(p => (
                <div key={p.id} className="flex items-center justify-between py-2 border-b border-[#E5E0D8] last:border-0">
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A]">{p.product_name}</p>
                    <p className="text-[10px] text-[#C5A059] tracking-wider">{p.product_code}</p>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-[#F2E8D5] text-[#C5A059] text-[10px]">{p.brand || 'N/A'}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[#5C5C5C] text-center py-4">No products yet</p>}
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-sm p-6" data-testid="recent-catalogs">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg text-[#1A1A1A]">Recent Catalogs</h3>
            <button onClick={() => navigate('/catalogs')} className="text-xs text-[#C5A059] hover:underline flex items-center gap-1">View all <ArrowRight size={12} /></button>
          </div>
          {stats.recent_catalogs.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_catalogs.map(c => (
                <div key={c.id} className="flex items-center justify-between py-2 border-b border-[#E5E0D8] last:border-0">
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A]">{c.title}</p>
                    <p className="text-[10px] text-[#5C5C5C]">{c.sections?.length || 0} sections</p>
                  </div>
                  <Badge className={`text-[10px] ${c.status === 'final' ? 'bg-[#2D6A4F] text-white' : c.status === 'archived' ? 'bg-[#E5E0D8] text-[#5C5C5C]' : 'bg-[#F2E8D5] text-[#C5A059]'}`}>{c.status}</Badge>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[#5C5C5C] text-center py-4">No catalogs yet</p>}
        </div>
      </div>
    </div>
  );
}
